import { toast } from "@/components/ui/use-toast";
import {
  CreditIndicator,
  Employee,
  PaymentTransactionType,
  TransactionStatus,
  TransactionType,
  TransactionsQuery,
} from "@common/utils/graphql";
import { type ClassValue, clsx } from "clsx";
import { UseFormReturn } from "react-hook-form";
import { extendTailwindMerge } from "tailwind-merge";
import { v5 as uuidV5 } from "uuid";

export const twMerge = extendTailwindMerge<"font-size">({
  extend: {
    classGroups: {
      "font-size": [
        "text-body-xs",
        "text-body-sm",
        "text-body-md",
        "text-body-lg",
        "text-body-xl",
        "text-body-2xl",
      ],
    },
  },
});
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getColorForState = (state: string) => {
  switch (state.toLowerCase()) {
    case "approved":
    case "autoapproved":
    case "auto-approved":
    case "auto approved":
      return "#54C186";
    case "rejected":
      return "#BE3E22";
    case "partially approved":
    case "partiallyapproved":
    case "pending":
      return "#D19C4C";
  }
  return "white";
};

export const getColorClassForState = (state: string) => {
  switch (state.toLowerCase()) {
    case "approved":
    case "autoapproved":
    case "auto-approved":
    case "auto approved":
      return "text-success";
    case "rejected":
      return "text-error";
    case "partially approved":
    case "partiallyapproved":
    case "pending":
      return "text-warning";
  }
  return "text-white";
};

export const getInitialsFromEmployee = (employee: Employee) => {
  let initials = "";
  if (employee.firstName === "(Previously") {
    return employee.workEmail[0];
  }
  if (employee?.firstName.length > 0) {
    initials += employee.firstName[0];
  }
  if (employee?.lastName.length > 0) {
    initials += employee.lastName[0];
  }
  return initials;
};

export const getInitialsFromDisplayName = (displayName: string) => {
  let initials = "";
  const name = displayName.split(" ");
  if (name.length > 0 && name[0].length > 0) {
    initials += name[0][0];
  }
  if (name.length > 1 && name[1].length > 0) {
    initials += name[1][0];
  }
  return initials;
};

export function compress(
  file: File,
  {
    quality = 1,
    maxWidth,
    maxHeight,
  }: { quality?: number; maxWidth: number; maxHeight: number },
): Promise<File> {
  return new Promise((resolve, reject) => {
    // only resize if file is an image
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const img = new Image();
    img.onerror = function () {
      reject(file);
    };
    img.onload = function () {
      const source = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };

      const target = {
        width: maxWidth || source.width,
        height: maxHeight || source.height,
      };
      const aspectRatio = Math.min(
        1,
        target.width / source.width,
        target.height / source.height,
      );

      target.width = source.width * aspectRatio;
      target.height = source.height * aspectRatio;

      // To resize the image is drawn to a canvas at the desired dimemsion then the blob of the canvas is abstracted
      const canvas = document.createElement("canvas");
      canvas.width = target.width;
      canvas.height = target.height;
      const context = canvas.getContext("2d");

      if (!context) {
        reject(new Error("Could not get canavas context"));
        return;
      }

      context.drawImage(img, 0, 0, target.width, target.height);
      canvas.toBlob(
        (blob) => {
          const newFile = new File([blob as BlobPart], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          resolve(newFile);
        },
        "image/png",
        quality,
      );
    };
    img.src = URL.createObjectURL(file);
  });
}

export const convertBase64 = (file: File): Promise<string | undefined> => {
  return new Promise((resolve, reject) => {
    compress(file, { maxWidth: 1200, maxHeight: 1200 })
      .then((newFile: File) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(newFile);

        fileReader.onload = () => {
          resolve((fileReader.result as string).split(",").pop());
        };

        fileReader.onerror = (error) => {
          reject(error);
        };
      })
      .catch(reject);
  });
};

export const receiptInputData = async (
  receipt: any,
): Promise<[Error | null, string | null | undefined]> => {
  if (!(receipt instanceof File)) return [null, undefined];
  try {
    const receiptFileData = await convertBase64(receipt);
    return [null, receiptFileData];
  } catch (error) {
    toast({
      variant: "destructive",
      description: "Failed to upload receipt, please try again.",
    });
    return [error as Error, null];
  }
};

const uuidNamespace = "72287ee6-cb5c-4b72-8021-10809192be80";
export const idempotencyKeyFromObject = (obj: any) =>
  uuidV5(JSON.stringify(obj), uuidNamespace);

export const MAX_EXPENSE_AMOUNT = 20000;

type TransactionsFromQuery = Extract<
  Required<TransactionsQuery["transactions"]>,
  { __typename: "QueryTransactionsSuccess" }
>;

type TransactionFromQuery =
  TransactionsFromQuery["data"]["allTransactions"][number];

type TransactionTypeTransactionFromQuery = Extract<
  Required<TransactionFromQuery>,
  { __typename: "Transaction" }
>;

type PaymentTransactionTypeTransactionFromQuery = Extract<
  Required<TransactionFromQuery>,
  { __typename: "PaymentTransaction" }
>;

export type ListItemProps =
  | TransactionTypeTransactionFromQuery
  | PaymentTransactionTypeTransactionFromQuery;

export const isPaymentTransactionType = (
  transaction?:
    | TransactionTypeTransactionFromQuery
    | PaymentTransactionTypeTransactionFromQuery,
): transaction is PaymentTransactionTypeTransactionFromQuery =>
  (transaction as PaymentTransactionTypeTransactionFromQuery)?.paymentStatus !==
  undefined;

export const isValidTransactionType = (
  transaction: TransactionFromQuery | ListItemProps,
): transaction is ListItemProps => transaction.__typename !== undefined;

export const getTransactionColor = (
  transaction: ListItemProps,
): "green" | "red" | undefined => {
  const isPaymentTransaction = isPaymentTransactionType(transaction);

  if (isPaymentTransaction) {
    if (transaction.paymentType === PaymentTransactionType.Regular) {
      return "green";
    }

    return "red";
  }

  if (transaction.creditIndicator === CreditIndicator.Credit) {
    return "green";
  } else if (
    transaction.transactionStatus === TransactionStatus.Declined ||
    transaction.transactionStatus === TransactionStatus.Reversed ||
    transaction.transactionType === TransactionType.BankCashbackAdjustment
  ) {
    return "red";
  }
};

export const hasDirtyFields = (form: UseFormReturn<any>) =>
  Object.values(form.formState.dirtyFields).some(
    (isDirtyField) => isDirtyField,
  );
